import { Dropdown } from '@edgebox/react-components';
import { IssueStatus, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { BP_WAITING_CONTROL_CLASSES } from './brandPresenceClasses';

/** The colour each status wears, as a pill; only the fixed one celebrates. */
export const ISSUE_STATUS_PILL_CLASSES: Record<IssueStatus, string> = {
  [IssueStatus.Open]: 'bg-sky-50 text-sky-700 ring-sky-200/80',
  [IssueStatus.Fixed]: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80',
  [IssueStatus.Ignored]: 'bg-slate-100 text-slate-600 ring-slate-200/80',
  [IssueStatus.FalsePositive]: 'bg-slate-100 text-slate-600 ring-slate-200/80',
};

const PILL_SHAPE =
  'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0 text-xs font-semibold ring-1 ring-inset';

/** The room the menu keeps from the edges of the frame, and from the control it opens at. */
const MENU_EDGE_PX = 8;
const MENU_GAP_PX = 4;

/**
 * The design system's menu is positioned absolutely by its own class, which this app's utilities make
 * `!important`, so an inline position never applies: the menu would stand inside the list's sideways
 * scrolling region and be cut by it. This selector outranks that class and fixes the menu in the frame.
 */
const MENU_FIXED_CLASS = '[&[data-bs-dropdown-menu]]:fixed';

/**
 * The menu stands exactly at its offsets. The design system's menu sets its top and bottom margins one
 * at a time as it measures itself, so they are overruled one at a time too: a shorthand beside them
 * would leave the browser to settle which one wins on every rerender.
 */
const MENU_NO_MARGINS: React.CSSProperties = { marginTop: 0, marginBottom: 0 };

/** Until it has been measured, the menu is laid out out of sight, so no frame shows it in the wrong place. */
const MENU_UNPLACED: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 'auto',
  bottom: 'auto',
  ...MENU_NO_MARGINS,
  visibility: 'hidden',
};

/**
 * Where the menu stands in the frame: beneath the control, or above it where the frame has no room
 * beneath, and moved in from the frame's side edge rather than past it.
 */
function menuPlacement(control: DOMRect, menu: { width: number; height: number }): { top: number; left: number } {
  const below = control.bottom + MENU_GAP_PX;
  const above = control.top - MENU_GAP_PX - menu.height;
  const fitsBelow = below + menu.height <= window.innerHeight - MENU_EDGE_PX;
  const top = fitsBelow || above < MENU_EDGE_PX ? below : above;
  const left = Math.max(MENU_EDGE_PX, Math.min(control.left, window.innerWidth - menu.width - MENU_EDGE_PX));
  return { top, left };
}

/**
 * The offsets that put the menu at a place in the frame, whatever its offsets are taken from: the frame
 * for a fixed element, or an ancestor that makes itself the origin, as a transformed one does. Where the
 * menu stands now, less the offsets it was given, is that origin, and the new offsets are taken from it.
 */
function fixedOffsets({
  place,
  now,
  given,
}: {
  place: { top: number; left: number };
  now: DOMRect;
  given: { top: number; left: number };
}): {
  top: number;
  left: number;
} {
  return { top: place.top - (now.top - given.top), left: place.left - (now.left - given.left) };
}

/** One status an issue can move to from where it stands, with the line that says what it means. */
export interface IIssueStatusChoice {
  id: string;
  label: string;
  explanation: string;
}

/** The status's own word, from the package. */
export function issueStatusLabel(status: IssueStatus): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueStatus', status);
}

/** The status as a pill that changes nothing: for a row no one here can change. */
export function IssueStatusPill({ status, title }: { status: IssueStatus; title?: string }) {
  return (
    <span className={`${PILL_SHAPE} ${ISSUE_STATUS_PILL_CLASSES[status]}`} title={title}>
      {issueStatusLabel(status)}
    </span>
  );
}

/**
 * A row's one status control: the status as a pill that opens the statuses the issue can move to,
 * each named with the line that says what it means.
 *
 * The list scrolls sideways inside its own region, which would cut a menu that opens inside it, so
 * the menu stands in the frame itself, at the control: it is placed where the control is when it
 * opens and follows it while the page or the list scrolls.
 *
 * While the row's own write runs the control stays reachable and says it is waiting; a choice made
 * then is handed to `onChoiceWhileWaiting` rather than acted on. The control takes the focus before a
 * choice acts, so a dialog the choice opens hands the focus back to a control that is still there.
 */
export function IssueStatusControl({
  label,
  status,
  choices,
  waiting,
  onChoose,
  onChoiceWhileWaiting,
}: {
  /** The control's accessible name, which says whose status it shows and what the status is. */
  label: string;
  status: IssueStatus;
  choices: IIssueStatusChoice[];
  waiting: boolean;
  onChoose: (choiceId: string) => void;
  onChoiceWhileWaiting: () => void;
}) {
  const toggleRef = useRef<HTMLButtonElement>(null);
  // The menu is in the document only while it is open, so its node says whether it is.
  const [menuNode, setMenuNode] = useState<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>(MENU_UNPLACED);

  useLayoutEffect(() => {
    if (!menuNode) {
      setMenuStyle(MENU_UNPLACED);
      return;
    }
    const place = () => {
      const toggle = toggleRef.current;
      if (!toggle) {
        return;
      }
      const now = menuNode.getBoundingClientRect();
      const given = { top: parseFloat(menuNode.style.top) || 0, left: parseFloat(menuNode.style.left) || 0 };
      const place = menuPlacement(toggle.getBoundingClientRect(), { width: now.width, height: now.height });
      setMenuStyle({ position: 'fixed', ...fixedOffsets({ place, now, given }), right: 'auto', bottom: 'auto', ...MENU_NO_MARGINS });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, { capture: true });
    };
  }, [menuNode]);

  return (
    <Dropdown align="start">
      {/* No variant: the pill's own colours are the whole look, and the design system's filled
      default would sit under them. */}
      <Dropdown.Toggle
        ref={toggleRef}
        type="button"
        variant=""
        caret={false}
        aria-label={label}
        aria-disabled={waiting || undefined}
        className={`${PILL_SHAPE} ${ISSUE_STATUS_PILL_CLASSES[status]} transition-shadow duration-150 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
          waiting ? BP_WAITING_CONTROL_CLASSES : ''
        }`}
      >
        {issueStatusLabel(status)}
        <FontAwesomeIcon icon={faChevronDown} className="text-[0.625rem]" aria-hidden="true" />
      </Dropdown.Toggle>
      <Dropdown.Menu
        ref={setMenuNode}
        className={`${MENU_FIXED_CLASS} w-64 max-w-[calc(100vw-2rem)] rounded-xl border-slate-200/80 p-1.5 shadow-card-hover`}
        style={menuStyle}
      >
        {choices.map((choice) => (
          <Dropdown.Item
            key={choice.id}
            as="button"
            className="block w-full whitespace-normal rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-slate-100/80"
            onClick={() => {
              toggleRef.current?.focus();
              if (waiting) {
                onChoiceWhileWaiting();
                return;
              }
              onChoose(choice.id);
            }}
          >
            <span className="block text-sm font-semibold text-slate-800">{choice.label}</span>
            <span className="block text-xs font-normal text-slate-500">{choice.explanation}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
